import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Users, FileLock2, ScrollText, AlertTriangle, Trash2, UserPlus, RotateCcw, Globe2,
} from "lucide-react";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "director" | "manager" | "staff";
  isAdmin: boolean;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  hasPassword: boolean;
}
interface AdminReport {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  route: string;
  isPublic: boolean;
  isActive: boolean;
}
interface AdminGrant { userId: string; reportPageId: string }
interface AuditEntry {
  id: string;
  timestamp: string;
  actorUserId: string | null;
  action: string;
  target: string | null;
  ip: string | null;
  detail: Record<string, unknown> | null;
}

const fetchJson = async (url: string) => {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};
const postJson = async (url: string, body: unknown) => {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? `${res.status}`);
  }
  return res.json();
};

export default function AdminPage() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" /> Admin
        </h1>
        <p className="text-sm text-muted-foreground">
          Users, report access, and audit log. Changes take effect immediately.
        </p>
      </div>
      <UsersSection />
      <ReportsSection />
      <PermissionsMatrix />
      <AuditLogSection />
    </div>
  );
}

function UsersSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ users: AdminUser[] }>({
    queryKey: ["admin-users"],
    queryFn: () => fetchJson("/api/admin/users"),
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [makeAdmin, setMakeAdmin] = useState(false);
  const [lastTempPassword, setLastTempPassword] = useState<{ email: string; temp: string } | null>(null);

  const create = useMutation({
    mutationFn: () => postJson("/api/admin/users", { email, name, isAdmin: makeAdmin }),
    onSuccess: (r: { tempPassword: string; user: AdminUser }) => {
      setLastTempPassword({ email: r.user.email, temp: r.tempPassword });
      setEmail("");
      setName("");
      setMakeAdmin(false);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast({ title: "Create failed", description: String(e), variant: "destructive" }),
  });

  const disable = useMutation({
    mutationFn: (id: string) => postJson(`/api/admin/users/${id}/disable`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const enable = useMutation({
    mutationFn: (id: string) => postJson(`/api/admin/users/${id}/enable`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const reset = useMutation({
    mutationFn: (id: string) => postJson(`/api/admin/users/${id}/reset-password`, {}),
    onSuccess: (r: { tempPassword: string }, id: string) => {
      const u = data?.users.find((x) => x.id === id);
      setLastTempPassword({ email: u?.email ?? "", temp: r.tempPassword });
    },
  });
  const roleChange = useMutation({
    mutationFn: ({ id, isAdmin }: { id: string; isAdmin: boolean }) =>
      postJson(`/api/admin/users/${id}/role`, { isAdmin }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Users</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" data-testid="admin-new-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" data-testid="admin-new-name" />
          </div>
          <div className="flex items-center gap-2 h-10">
            <Checkbox id="adm" checked={makeAdmin} onCheckedChange={(c) => setMakeAdmin(Boolean(c))} />
            <Label htmlFor="adm" className="cursor-pointer text-sm">Make admin</Label>
          </div>
          <Button onClick={() => create.mutate()} disabled={!email || !name || create.isPending} data-testid="admin-create-user">
            <UserPlus className="h-4 w-4 mr-2" /> Create user
          </Button>
        </div>

        {lastTempPassword && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-300/60 px-3 py-2 text-sm">
            <div className="font-semibold mb-1">One-time temporary password for {lastTempPassword.email}</div>
            <code className="px-2 py-1 bg-background rounded select-all font-mono text-sm" data-testid="admin-temp-password">{lastTempPassword.temp}</code>
            <p className="text-xs mt-2 text-muted-foreground">Share securely. The user will be forced to change it on first login. This is the only time it will be shown.</p>
            <Button variant="ghost" size="sm" onClick={() => setLastTempPassword(null)} className="mt-2">Dismiss</Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1.5">Email</th>
                <th>Name</th>
                <th>Admin</th>
                <th>Active</th>
                <th>Pw change due</th>
                <th>Last login</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={7} className="py-4 text-muted-foreground">Loading…</td></tr>)}
              {data?.users.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="py-1.5 pr-2 font-mono text-xs">{u.email}</td>
                  <td>{u.name}</td>
                  <td>
                    <Checkbox
                      checked={u.isAdmin}
                      onCheckedChange={(c) => roleChange.mutate({ id: u.id, isAdmin: Boolean(c) })}
                      data-testid={`admin-isadmin-${u.email}`}
                    />
                  </td>
                  <td>{u.isActive ? "Yes" : <span className="text-red-600">No</span>}</td>
                  <td>{u.mustChangePassword ? "Yes" : "—"}</td>
                  <td className="text-xs text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("en-GB") : "—"}</td>
                  <td className="text-right space-x-1">
                    {u.isActive ? (
                      <Button size="sm" variant="ghost" onClick={() => disable.mutate(u.id)} data-testid={`admin-disable-${u.email}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Disable
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => enable.mutate(u.id)}>Enable</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => reset.mutate(u.id)} data-testid={`admin-reset-${u.email}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Reset pw
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportsSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ reports: AdminReport[] }>({
    queryKey: ["admin-reports"],
    queryFn: () => fetchJson("/api/admin/reports"),
  });

  const setPublic = useMutation({
    mutationFn: ({ id, isPublic }: { id: string; isPublic: boolean }) =>
      postJson(`/api/admin/reports/${id}/visibility`, { isPublic }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-reports"] }),
    onError: (e) => toast({ title: "Update failed", description: String(e), variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileLock2 className="h-5 w-5" /> Reports</CardTitle>
        <p className="text-xs text-muted-foreground">Public reports are visible to anyone on the internet without login — leave OFF unless you mean it.</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Title</th>
                <th>Slug</th>
                <th>Route</th>
                <th>Description</th>
                <th>Public</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={5} className="py-4 text-muted-foreground">Loading…</td></tr>)}
              {data?.reports.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-1.5 pr-2 font-medium">{r.title}</td>
                  <td className="font-mono text-xs">{r.slug}</td>
                  <td className="font-mono text-xs">{r.route}</td>
                  <td className="text-xs text-muted-foreground max-w-md">{r.description}</td>
                  <td>
                    <PublicToggle report={r} onChange={(isPublic) => setPublic.mutate({ id: r.id, isPublic })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function PublicToggle({ report, onChange }: { report: AdminReport; onChange: (v: boolean) => void }) {
  const [confirm, setConfirm] = useState(false);
  if (confirm) {
    return (
      <div className="space-y-1 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700 p-2 text-xs">
        <div className="flex items-center gap-1.5 font-semibold text-red-700 dark:text-red-300">
          <AlertTriangle className="h-3.5 w-3.5" /> Are you sure?
        </div>
        <p className="text-red-700 dark:text-red-300">
          This makes <b>{report.title}</b> viewable by anyone on the internet with no login. Do not do this for anything with commercial, financial, customer or staff data.
        </p>
        <div className="flex gap-1 pt-1">
          <Button size="sm" variant="destructive" onClick={() => { setConfirm(false); onChange(true); }}>
            <Globe2 className="h-3.5 w-3.5 mr-1" /> Yes, make public
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <Checkbox
      checked={report.isPublic}
      onCheckedChange={(c) => {
        const v = Boolean(c);
        if (v) setConfirm(true);
        else onChange(false);
      }}
      data-testid={`admin-public-${report.slug}`}
    />
  );
}

function PermissionsMatrix() {
  const queryClient = useQueryClient();
  const users = useQuery<{ users: AdminUser[] }>({ queryKey: ["admin-users"], queryFn: () => fetchJson("/api/admin/users") });
  const reports = useQuery<{ reports: AdminReport[] }>({ queryKey: ["admin-reports"], queryFn: () => fetchJson("/api/admin/reports") });
  const grants = useQuery<{ grants: AdminGrant[] }>({ queryKey: ["admin-grants"], queryFn: () => fetchJson("/api/admin/permissions") });

  const grantSet = useMemo(() => {
    const s = new Set<string>();
    grants.data?.grants.forEach((g) => s.add(`${g.userId}::${g.reportPageId}`));
    return s;
  }, [grants.data]);

  const toggle = useMutation({
    mutationFn: ({ userId, reportPageId, granted }: { userId: string; reportPageId: string; granted: boolean }) =>
      postJson("/api/admin/permissions", { userId, reportPageId, granted }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-grants"] }),
  });

  if (users.isLoading || reports.isLoading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><FileLock2 className="h-5 w-5" /> Permissions matrix</CardTitle>
        <p className="text-xs text-muted-foreground">
          Tick to grant a user access to a report. Admins (highlighted) implicitly see everything.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-background text-left py-1 pr-2 font-medium border-b">User</th>
                {reports.data?.reports.map((r) => (
                  <th key={r.id} className="px-2 py-1 text-xs whitespace-nowrap border-b" title={r.description ?? r.title}>
                    <div className="rotate-[-30deg] origin-left translate-y-2">{r.title}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.data?.users.map((u) => (
                <tr key={u.id} className={`border-t ${u.isAdmin ? "bg-emerald-50/40 dark:bg-emerald-950/20" : ""}`}>
                  <td className="sticky left-0 bg-background py-1.5 pr-2 font-mono text-xs">
                    {u.email}{u.isAdmin && <span className="ml-1 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold">ADMIN</span>}
                  </td>
                  {reports.data?.reports.map((r) => {
                    const key = `${u.id}::${r.id}`;
                    return (
                      <td key={r.id} className="px-2 py-1 text-center">
                        <Checkbox
                          disabled={u.isAdmin}
                          checked={u.isAdmin || grantSet.has(key)}
                          onCheckedChange={(c) =>
                            toggle.mutate({ userId: u.id, reportPageId: r.id, granted: Boolean(c) })
                          }
                          data-testid={`grid-${u.email}-${r.slug}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AuditLogSection() {
  const { data, isLoading } = useQuery<{ entries: AuditEntry[] }>({
    queryKey: ["admin-audit"],
    queryFn: () => fetchJson("/api/admin/audit?limit=200"),
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ScrollText className="h-5 w-5" /> Audit log <span className="text-xs font-normal text-muted-foreground">(last 200)</span></CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Timestamp</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Target</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={5} className="py-4 text-muted-foreground">Loading…</td></tr>)}
            {data?.entries.map((e) => (
              <tr key={e.id} className="border-t">
                <td className="py-1 pr-2 font-mono">{new Date(e.timestamp).toLocaleString("en-GB")}</td>
                <td className="font-mono">{e.action}</td>
                <td className="font-mono text-muted-foreground">{e.actorUserId?.slice(0, 8) ?? "—"}</td>
                <td className="font-mono">{e.target ?? "—"}</td>
                <td className="font-mono text-muted-foreground">{e.ip ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
